import React, { useState, useEffect } from 'react';
import { FiShield, FiCheck, FiX, FiEye, FiLoader, FiAlertCircle, FiClock, FiUser } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface KYCSubmission {
  id: string;
  user_id: string;
  user_email: string;
  full_name: string;
  date_of_birth: string;
  nationality: string;
  phone_number: string;
  address_line1: string;
  city: string;
  country: string;
  id_type: string;
  id_number: string;
  id_document_url: string;
  selfie_url: string;
  proof_of_address_url: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  rejection_reason: string;
  admin_notes: string;
  created_at: string;
  reviewed_at: string;
}

interface KYCStats {
  total: number;
  pending: number;
  under_review: number;
  approved: number;
  rejected: number;
}

const KYCSubmissionsPanel: React.FC = () => {
  const [submissions, setSubmissions] = useState<KYCSubmission[]>([]);
  const [stats, setStats] = useState<KYCStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<KYCSubmission | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [docUrls, setDocUrls] = useState<{
    idDocument: string | null;
    selfie: string | null;
    proofOfAddress: string | null;
  }>({ idDocument: null, selfie: null, proofOfAddress: null });

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, [filter]);

  useEffect(() => {
    const loadSignedUrls = async () => {
      if (!selectedSubmission) {
        setDocUrls({ idDocument: null, selfie: null, proofOfAddress: null });
        return;
      }

      const buildPath = (value?: string | null) => {
        if (!value) return null;
        const marker = '/kyc-documents/';
        const idx = value.indexOf(marker);
        return idx !== -1 ? value.substring(idx + marker.length) : value;
      };

      try {
        const [idRes, selfieRes, proofRes] = await Promise.all([
          selectedSubmission.id_document_url
            ? supabase.storage
                .from('kyc-documents')
                .createSignedUrl(buildPath(selectedSubmission.id_document_url)!, 60 * 10)
            : Promise.resolve({ data: null }),
          selectedSubmission.selfie_url
            ? supabase.storage
                .from('kyc-documents')
                .createSignedUrl(buildPath(selectedSubmission.selfie_url)!, 60 * 10)
            : Promise.resolve({ data: null }),
          selectedSubmission.proof_of_address_url
            ? supabase.storage
                .from('kyc-documents')
                .createSignedUrl(buildPath(selectedSubmission.proof_of_address_url)!, 60 * 10)
            : Promise.resolve({ data: null }),
        ]);

        setDocUrls({
          idDocument: idRes.data?.signedUrl ?? null,
          selfie: selfieRes.data?.signedUrl ?? null,
          proofOfAddress: proofRes.data?.signedUrl ?? null,
        });
      } catch (err) {
        console.error('Failed to create signed URLs for KYC documents', err);
        setDocUrls({ idDocument: null, selfie: null, proofOfAddress: null });
      }
    };

    loadSignedUrls();
  }, [selectedSubmission]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('kyc_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSubmissions(data || []);
    } catch (err) {
      console.error('Failed to fetch KYC submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_kyc_stats');
      if (error) throw error;
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch KYC stats:', err);
    }
  };

  const updateStatus = async (submissionId: string, status: string) => {
    setProcessing(submissionId);
    try {
      const { data, error } = await supabase.rpc('admin_update_kyc_status', {
        p_submission_id: submissionId,
        p_status: status,
        p_rejection_reason: status === 'rejected' ? rejectionReason : null,
        p_admin_notes: adminNotes || null
      });

      if (error) throw error;
      
      await fetchSubmissions();
      await fetchStats();
      setSelectedSubmission(null);
      setRejectionReason('');
      setAdminNotes('');
    } catch (err) {
      console.error('Failed to update KYC status:', err);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      under_review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      under_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-gray-400">Total Submissions</div>
          </div>
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
            <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
            <div className="text-sm text-yellow-400/70">Pending</div>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
            <div className="text-2xl font-bold text-blue-400">{stats.under_review}</div>
            <div className="text-sm text-blue-400/70">Under Review</div>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <div className="text-2xl font-bold text-green-400">{stats.approved}</div>
            <div className="text-sm text-green-400/70">Approved</div>
          </div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <div className="text-2xl font-bold text-red-400">{stats.rejected}</div>
            <div className="text-sm text-red-400/70">Rejected</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'under_review', 'approved', 'rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1).replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FiLoader className="animate-spin text-purple-500" size={24} />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FiShield size={48} className="mx-auto mb-4 opacity-50" />
            <p>No KYC submissions found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="p-4 hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      <FiUser className="text-gray-400" size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{submission.full_name}</span>
                        {getStatusBadge(submission.status)}
                      </div>
                      <div className="text-sm text-gray-400">{submission.user_email}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <div className="text-gray-400">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-gray-500">{submission.country || 'No country'}</div>
                    </div>
                    
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <FiEye className="text-gray-300" size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <FiShield className="text-purple-500" size={24} />
                <h3 className="text-xl font-semibold text-white">KYC Review</h3>
                {getStatusBadge(selectedSubmission.status)}
              </div>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-400 hover:text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Info */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Personal Information</h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-800/50 rounded-xl p-4">
                  <div>
                    <span className="text-gray-500 text-sm">Full Name</span>
                    <p className="text-white">{selectedSubmission.full_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Email</span>
                    <p className="text-white">{selectedSubmission.user_email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Date of Birth</span>
                    <p className="text-white">{selectedSubmission.date_of_birth || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Nationality</span>
                    <p className="text-white">{selectedSubmission.nationality || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Phone</span>
                    <p className="text-white">{selectedSubmission.phone_number || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Country</span>
                    <p className="text-white">{selectedSubmission.country || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Address</h4>
                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-white">
                    {selectedSubmission.address_line1 || 'No address provided'}
                    {selectedSubmission.city && `, ${selectedSubmission.city}`}
                    {selectedSubmission.country && `, ${selectedSubmission.country}`}
                  </p>
                </div>
              </div>

              {/* ID Documents */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Identity Documents</h4>
                <div className="grid grid-cols-2 gap-4 bg-gray-800/50 rounded-xl p-4">
                  <div>
                    <span className="text-gray-500 text-sm">ID Type</span>
                    <p className="text-white capitalize">{selectedSubmission.id_type?.replace('_', ' ') || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">ID Number</span>
                    <p className="text-white">{selectedSubmission.id_number || 'Not provided'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {selectedSubmission.id_document_url && docUrls.idDocument && (
                    <div className="bg-gray-800 rounded-xl p-3">
                      <span className="text-sm text-gray-400 block mb-2">ID Document</span>
                      <a
                        href={docUrls.idDocument}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={docUrls.idDocument}
                          alt="ID Document"
                          className="w-full h-48 object-cover rounded-lg border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer"
                        />
                      </a>
                    </div>
                  )}
                  {selectedSubmission.selfie_url && docUrls.selfie && (
                    <div className="bg-gray-800 rounded-xl p-3">
                      <span className="text-sm text-gray-400 block mb-2">Selfie</span>
                      <a
                        href={docUrls.selfie}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={docUrls.selfie}
                          alt="Selfie"
                          className="w-full h-48 object-cover rounded-lg border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer"
                        />
                      </a>
                    </div>
                  )}
                  {selectedSubmission.proof_of_address_url && docUrls.proofOfAddress && (
                    <div className="bg-gray-800 rounded-xl p-3 col-span-2">
                      <span className="text-sm text-gray-400 block mb-2">Proof of Address</span>
                      <a
                        href={docUrls.proofOfAddress}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={docUrls.proofOfAddress}
                          alt="Proof of Address"
                          className="w-full h-48 object-cover rounded-lg border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                  rows={3}
                  placeholder="Internal notes (not shown to user)"
                />
              </div>

              {/* Rejection Reason */}
              {selectedSubmission.status !== 'approved' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Rejection Reason (if rejecting)</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                    rows={2}
                    placeholder="Reason for rejection (shown to user)"
                  />
                </div>
              )}

              {/* Actions */}
              {selectedSubmission.status !== 'approved' && (
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => updateStatus(selectedSubmission.id, 'under_review')}
                    disabled={processing === selectedSubmission.id}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FiClock size={18} />
                    Mark Under Review
                  </button>
                  <button
                    onClick={() => updateStatus(selectedSubmission.id, 'approved')}
                    disabled={processing === selectedSubmission.id}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FiCheck size={18} />
                    Approve
                  </button>
                  <button
                    onClick={() => updateStatus(selectedSubmission.id, 'rejected')}
                    disabled={processing === selectedSubmission.id || !rejectionReason}
                    className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FiX size={18} />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCSubmissionsPanel;
